import { getCountries } from 'libphonenumber-js';
import type { SearchSelectOption } from '@/hotdesk-ui/components/shared/SearchSelect';

type Country = {
    code: string;
    /** In the page language — what the user sees. */
    label: string;
    /** English — what the form submits, as before. */
    english: string;
};

/** Lowercase, accents stripped, so "turkiye" finds "Türkiye". */
const fold = (s: string) =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const cache = new Map<string, Country[]>();

/**
 * Every country/territory the phone input knows (ISO-2), named via
 * Intl.DisplayNames in `locale` and in English.
 */
function countriesFor(locale: string): Country[] {
    const cached = cache.get(locale);
    if (cached) return cached;
    const local = new Intl.DisplayNames([locale], { type: 'region' });
    const english = new Intl.DisplayNames(['en'], { type: 'region' });
    const list = getCountries()
        .map((code) => ({
            code,
            label: local.of(code) ?? code,
            english: english.of(code) ?? code
        }))
        // Codes without a real name come back as the code itself.
        .filter((c) => c.english !== c.code);
    cache.set(locale, list);
    return list;
}

/** Lower is better: exact, prefix, word prefix, then anywhere. Null = no match. */
function matchRank(name: string, query: string): number | null {
    const n = fold(name);
    if (n === query) return 0;
    if (n.startsWith(query)) return 1;
    if (n.split(/[\s\-'’(),&.]+/).some((word) => word.startsWith(query))) return 2;
    return n.includes(query) ? 3 : null;
}

const toOption = (c: Country): SearchSelectOption => ({
    id: c.code,
    label: c.label,
    value: c.english
});

/**
 * Countries matching `query` in the page language or in English, best first;
 * an empty query lists every country A–Z. The option shows the localized
 * name and submits the English one.
 */
export function searchCountries(
    query: string,
    locale: string
): SearchSelectOption[] {
    const q = fold(query);
    if (!q) {
        return [...countriesFor(locale)]
            .sort((a, b) => a.label.localeCompare(b.label, locale))
            .map(toOption);
    }
    return countriesFor(locale)
        .map((c) => {
            const ranks = [matchRank(c.label, q), matchRank(c.english, q)].filter(
                (r): r is number => r !== null
            );
            return { c, rank: ranks.length ? Math.min(...ranks) : null };
        })
        .filter((x): x is { c: Country; rank: number } => x.rank !== null)
        .sort(
            (a, b) =>
                a.rank - b.rank ||
                a.c.label.length - b.c.label.length ||
                a.c.label.localeCompare(b.c.label, locale)
        )
        .map(({ c }) => toOption(c));
}
